import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Search, X, Phone, Mail, StickyNote } from 'lucide-react';
import { api } from '../../../services/api';
import { useWorkspace } from '../WorkspaceContext';
import { formatDateKeyFromMs, formatDisplayDate, formatTime } from '../utils/datetime';

const emptyForm = { name: '', email: '', phone: '', notes: '' };

const Clients = () => {
  const { activeLocation } = useWorkspace();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadClients = useCallback(async () => {
    if (!activeLocation) return;
    setLoading(true);
    const result = await api.getWorkspaceClients(activeLocation.id);
    if (result.error) {
      setError(result.error);
      setClients([]);
    } else {
      setError('');
      setClients(Array.isArray(result.data) ? result.data : []);
    }
    setLoading(false);
  }, [activeLocation]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    setSelectedId(null);
  }, [activeLocation?.id]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    (async () => {
      const result = await api.getWorkspaceClient(selectedId);
      if (!cancelled) {
        if (result.error) {
          setError(result.error);
        } else {
          setDetail(result.data);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((client) =>
      [client.name, client.email, client.phone, client.notes]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [clients, query]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (client) => {
    setEditingId(client.id);
    setForm({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      notes: client.notes || '',
    });
    setFormOpen(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      notes: form.notes.trim() || null,
      locationId: activeLocation?.id,
    };

    const result = editingId
      ? await api.updateWorkspaceClient(editingId, payload)
      : await api.createWorkspaceClient(payload);

    setSaving(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    setFormOpen(false);
    await loadClients();
    if (result.data?.id) {
      setSelectedId(result.data.id);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-8 sm:px-6 sm:py-10 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-saffron-600">Clients</p>
          <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-ink-900">
            Client book
          </h1>
          <p className="mt-2 max-w-xl text-sm text-ink-700/80">
            Everyone who books or visits — searchable, with notes that follow every appointment.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 bg-saffron-500 px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
        >
          <Plus className="h-4 w-4" />
          Add client
        </button>
      </div>

      {error && (
        <div className="mt-6 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
        <div className="bg-sand-50 ring-1 ring-ink-900/10">
          <div className="border-b border-ink-900/10 p-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-600/50" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, phone or email"
                className="w-full bg-white py-3 pl-10 pr-4 text-sm text-ink-900 ring-1 ring-inset ring-ink-900/15 placeholder:text-ink-600/40 focus:outline-none focus:ring-2 focus:ring-saffron-500"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <p className="font-display text-2xl font-light text-ink-900">No clients yet</p>
              <p className="mt-2 text-sm text-ink-700/70">
                Add someone manually, or share your booking link and they&rsquo;ll appear here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-ink-900/10">
              {filtered.map((client) => {
                const active = selectedId === client.id;
                return (
                  <li key={client.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(client.id)}
                      className={`flex w-full items-start justify-between gap-4 px-5 py-4 text-left transition-colors ${
                        active ? 'bg-ink-900 text-sand-50' : 'hover:bg-white'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">{client.name}</p>
                        <p className={`mt-1 truncate text-sm ${active ? 'text-sand-300' : 'text-ink-600/70'}`}>
                          {client.phone || client.email || 'No contact details'}
                        </p>
                      </div>
                      <span className={`flex-none text-xs tabular-nums ${active ? 'text-sand-300' : 'text-ink-600/60'}`}>
                        {client.visitCount || 0} visits
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <aside className="bg-sand-50 p-6 ring-1 ring-ink-900/10">
          {!detail ? (
            <p className="text-sm text-ink-600/70">Select a client to see their profile and visit history.</p>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-display text-2xl font-light text-ink-900">{detail.name}</h2>
                  <p className="mt-1 text-xs uppercase tracking-[0.16em] text-ink-600/60">
                    {detail.visitCount || 0} visits
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(detail)}
                  className="text-sm font-medium text-ink-900 underline decoration-saffron-500 decoration-2 underline-offset-4"
                >
                  Edit
                </button>
              </div>

              <dl className="mt-6 space-y-3 text-sm">
                {detail.phone && (
                  <div className="flex items-center gap-3 text-ink-800">
                    <Phone className="h-4 w-4 text-ink-600/50" />
                    {detail.phone}
                  </div>
                )}
                {detail.email && (
                  <div className="flex items-center gap-3 text-ink-800">
                    <Mail className="h-4 w-4 text-ink-600/50" />
                    {detail.email}
                  </div>
                )}
                {detail.notes && (
                  <div className="flex items-start gap-3 text-ink-800">
                    <StickyNote className="mt-0.5 h-4 w-4 text-ink-600/50" />
                    <span>{detail.notes}</span>
                  </div>
                )}
              </dl>

              <div className="mt-8 border-t border-ink-900/10 pt-5">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ink-600/60">
                  Recent appointments
                </p>
                {!detail.appointments?.length ? (
                  <p className="mt-3 text-sm text-ink-600/70">No appointments yet.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {detail.appointments.slice(0, 8).map((appt) => (
                      <li key={appt.id} className="bg-white px-3 py-2.5 text-sm ring-1 ring-ink-900/10">
                        <p className="font-medium text-ink-900">
                          {appt.service?.name || 'Visit'}
                          {appt.status === 'cancelled' ? ' · Cancelled' : ''}
                          {appt.status === 'completed' ? '' : appt.status === 'arrived' ? ' · On floor' : ''}
                        </p>
                        <p className="text-ink-600/70">
                          {formatDateKeyFromMs(appt.startAt) === formatDateKeyFromMs(Date.now())
                            ? 'Today'
                            : formatDisplayDate(formatDateKeyFromMs(appt.startAt))}
                          {' · '}
                          {formatTime(appt.startAt)}
                          {appt.staff?.name ? ` · ${appt.staff.name}` : ''}
                        </p>
                        {appt.notes ? (
                          <p className="mt-1 text-xs text-ink-600/60">{appt.notes}</p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-950/50 p-4 sm:items-center">
          <div className="w-full max-w-md bg-sand-50 p-6 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.55)] ring-1 ring-ink-900/10 sm:p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-light text-ink-900">
                {editingId ? 'Edit client' : 'Add client'}
              </h2>
              <button type="button" onClick={() => setFormOpen(false)} aria-label="Close">
                <X className="h-5 w-5 text-ink-700" />
              </button>
            </div>

            <form className="mt-6 space-y-4" onSubmit={handleSave}>
              {[
                { id: 'name', label: 'Full name', required: true },
                { id: 'phone', label: 'Phone', type: 'tel' },
                { id: 'email', label: 'Email', type: 'email' },
              ].map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={field.id}
                    className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
                  >
                    {field.label}
                  </label>
                  <input
                    id={field.id}
                    type={field.type || 'text'}
                    required={field.required}
                    value={form[field.id]}
                    onChange={(e) => setForm((prev) => ({ ...prev, [field.id]: e.target.value }))}
                    className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  />
                </div>
              ))}
              <div>
                <label
                  htmlFor="notes"
                  className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
                >
                  Notes
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-saffron-500 px-5 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
              >
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add client'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
